
import OverlapLayer from "@/components/OverlapLayer/OverlapLayer.vue";
import { API_ROUTES } from "@/utils/API";
import { chatwootLoad, chatwootShow, chatwootShutdown } from "@/utils/chatwoot";
import { Component, Vue, Watch } from "vue-property-decorator";
import { SMApp, SMCases, SMUser } from "./store";
import { ComplianceStatus } from "@/models/company";
import axios from "axios";

const LOGIN_ROUTE = "/auth/login";

const PRODUCTION_MODE = "production";
const PRODUCTION_ENVIRONMENT_BASE_URL = "https://compliance.nationbetter.uk";

const COMPLIANCE_EXPIRED_ERROR_TYPE = "compliance-expired";
const DASHBOAAR_TAB_TYPE = "dashboard";

@Component({
  components: {
    OverlapLayer,
  },
})
class App extends Vue {
  async getProfileLink(origin: string) {
    const u: any = SMUser.user;
    const companyId = u?.company_id ?? u?.company?.id ?? null;
    const employeeId = u?.ID ?? u?.id ?? u?.user_id ?? null;

    if (SMUser.isCandidate && SMCases.caseId) {
      return `${origin}/#/expert/cases/${SMCases.caseId}/dashboard/0`;
    }

    if (SMUser.isEmployee && companyId && employeeId) {
      return `${origin}/#/expert/companies/${companyId}/employees/${employeeId}/summary`;
    }

    if (SMUser.isEmployer && companyId) return `${origin}/#/expert/companies/${companyId}/summary`;

    return window.location.href;
  }

  get showChatwoot() {
    if (SMUser.isExpert) return false;

    const isOnProduction =
      process.env.NODE_ENV === PRODUCTION_MODE &&
      window.location.origin === PRODUCTION_ENVIRONMENT_BASE_URL;

    return isOnProduction;
  }

  get authOnServer() {
    return SMApp.authOnServer;
  }

  @Watch("authOnServer", { immediate: true })
  authOnServerChanged(newValue: boolean) {
    if (!newValue) return;

    if (SMUser?.user) return;

    return this.$auth.logout({ redirect: LOGIN_ROUTE });
  }

  async fetchChatwoot() {
    try {
      await this.axios.get(API_ROUTES.CHATWOOT_CONTACT);
    } catch (error) {
      console.error("Failed to call chatwoot endpoint after init", error);
    }
  }

  async installChatwoot() {
    if (!this.showChatwoot || !SMUser?.user) return;

    const u: any = SMUser.user;
    const id = u?.ID ?? u?.id ?? u?.user_id ?? u?.email ?? "";
    const email = u?.email ?? "";
    const name = [u?.first_name, u?.last_name].filter(Boolean).join(" ");
    const role = u?.role ?? "";
    const companyName = u?.company?.CompanyProfile?.name ?? "";
    const companyId = u?.company_id ?? null;
    const complianceSubscriptionStatus = u?.company?.subscription_status ?? "Trial";
    const complianceStatus =
      u?.company?.compliance_status === ComplianceStatus.Compliant ? "compliant" : "not compliant";
    const origin = window.location.origin;
    const profileLink = await this.getProfileLink(origin);

    window.addEventListener(
      "chatwoot:ready",
      () => {
        chatwootShow();
      },
      { once: true } as any
    );

    const isChatwootUserSet = await chatwootLoad({
      user: {
        id: String(id || ""),
        email,
        name: name || undefined,
        company_name: companyName || undefined,
        chatwoot_identifier: u?.chatwoot_identifier || undefined,
        customAttributes: {
          full_name: name || null,
          email_address: email || null,
          role: role || null,
          company_name: companyName || null,
          company_id: companyId,
          profile_link: profileLink || null,
          compliance_subscription_status: complianceSubscriptionStatus || null,
          compliance_status: complianceStatus,
        },
      },
    });

    if (isChatwootUserSet) {
      await this.fetchChatwoot();
    } else {
      console.error("[Chatwoot][init] Skipping contact API because setUser was not confirmed");
    }

    chatwootShow();
  }

  get userEmail(): string {
    return SMUser?.user?.email || "";
  }

  @Watch("userEmail", { immediate: true })
  userEmailChanged(value: string) {
    if (value) {
      this.installChatwoot();
    } else {
      chatwootShutdown();
    }
  }

  beforeCreate() {
    Vue.axios.get(API_ROUTES.STATUS);
  }

  setupErrorHandlers() {
    this.$http.interceptors.response.use(
      (response) => response,
      (error) => {
        if (axios.isCancel(error)) return Promise.reject(error);

        const errorStatus = error.response?.status;
        const errorType = error.response?.data?.type;
        const errorMessage = error.response?.data?.message;

        if (!errorStatus) return;

        if (errorStatus === 403) {
          if (errorType === COMPLIANCE_EXPIRED_ERROR_TYPE) {
            this.$root.$emit("show-error-popup", {
              status: errorStatus,
              message: errorMessage,
            });
          }
        }

        return Promise.reject(error);
      }
    );
  }

  beforeMount() {
    this.setupErrorHandlers?.();
  }

  mounted() {
    if (!this.showChatwoot) chatwootShutdown();

    SMApp.updateRefreshDate();
  }

  updated() {
    SMApp.updateRefreshDate();
  }
}

export default App;
